import { queryOptions, useMutation } from '@tanstack/react-query';

import { parse } from '@tinyhttp/content-disposition';
import { api } from '#client';
import { handleError, parseSchema } from '#client/validation';

import { TransactionError } from './errors.types';
import {
	DocumentTypes,
	FileUploadResult,
	SalesDocumentPostResponse,
	SalesDocumentPostResponseSchema,
	SalesFilesForClaimSchema,
} from './file.types';

export const documentQueries = {
	documentTypes: () =>
		queryOptions({
			queryKey: ['document', 'types'],
			queryFn: () => getDocumentTypes(),
		}),
	salesDocuments: (claimId: string) =>
		queryOptions({
			queryKey: ['salesDocuments', claimId],
			queryFn: () => getSalesDocumentsForClaim(claimId),
		}),
};

export const uploadFile = async (
	file: File,
): Promise<FileUploadResult | undefined> => {
	const formData = new FormData();
	formData.append('file', file);

	try {
		return await api
			.post('/file', {
				body: formData,
			})
			.then(parseSchema(FileUploadResult));
	} catch (err) {
		await handleError(['file'])(err);
		throw err;
	}
};

export const useUploadFile = (onSuccess: (arg: FileUploadResult) => void) => {
	return useMutation<
		FileUploadResult | undefined,
		TransactionError<{ file: string }>,
		File
	>({
		mutationFn: (file: File) => uploadFile(file),
		onSuccess: (arg: FileUploadResult | undefined) => {
			if (arg) {
				onSuccess(arg);
			}
		},
		onError: (err) => {
			console.log({ err });
			return err;
		},
	});
};

export const getDocumentTypes = (): Promise<DocumentTypes> =>
	api.get(`/file/documentTypes`).then(parseSchema(DocumentTypes));

export const getDocumentFile = (fileId: string) => api.get(`/file/${fileId}`);

export const downloadDocumentFile = async (fileId: string) => {
	const response = await getDocumentFile(fileId);

	const filename = parse(response.headers.get('content-disposition') as string)
		.parameters.filename;
	const type = response.headers.get('content-type') || 'application/pdf';

	return new File(
		[await response.blob()],
		(typeof filename === 'string' && filename) || `file-${fileId}.zip`,
		{ type },
	);
};

export const deleteDocument = (documentId: string) => {
	return api.delete(`/file/${documentId}`);
};

// SALES DOCUMENTS
export const downloadSalesDocumentFile = async (salesDocumentId: string) => {
	const response = await api.get(`/file/sales-document/${salesDocumentId}`);

	const filename = parse(response.headers.get('content-disposition') as string)
		.parameters.filename;
	const type = response.headers.get('content-type') || 'application/pdf';

	return new File(
		[await response.blob()],
		(typeof filename === 'string' && filename) || `file-${salesDocumentId}.zip`,
		{ type },
	);
};

export const getSalesDocumentsForClaim = (claimId: string) => {
	return api
		.get(`/file/${claimId}/sales-documents-for-claim`)
		.then(parseSchema(SalesFilesForClaimSchema));
};

export const uploadSalesDocument = async (
	claimId: string,
	files: File[],
): Promise<SalesDocumentPostResponse | undefined> => {
	const formData = new FormData();
	files.forEach((file) => {
		formData.append('files', file);
	});

	try {
		return await api
			.post(`/file/${claimId}/sales-documents`, {
				body: formData,
			})
			.then(parseSchema(SalesDocumentPostResponseSchema));
	} catch (err) {
		await handleError(['file'])(err);
		throw err;
	}
};

export const useUploadSalesDocument = (
	claimId: string,
	onSuccess?: (arg: SalesDocumentPostResponse) => void,
) => {
	return useMutation<
		SalesDocumentPostResponse | undefined,
		TransactionError<{ file: string }>,
		File[]
	>({
		mutationFn: (files: File[]) => uploadSalesDocument(claimId, files),
		onSuccess: (arg: SalesDocumentPostResponse | undefined) => {
			if (arg && onSuccess) {
				onSuccess(arg);
			}
		},
		onError: (err) => {
			console.log({ err });
		},
	});
};

export const deleteSalesDocument = async (salesDocumentId: string) => {
	await api.delete(`/file/sales-document/${salesDocumentId}`);
};

export const useDeleteSalesDocument = (onSuccess?: () => void) => {
	return useMutation<void, Error, string>({
		mutationFn: (salesDocumentId: string) =>
			deleteSalesDocument(salesDocumentId),
		onSuccess: () => {
			if (onSuccess) {
				onSuccess();
			}
		},
		onError: (err) => {
			console.error('Delete sales document error:', err);
		},
	});
};
